// T6-BOARD-NOTE: the board's owner (`board.lead_seat_id`), picked from the company's seats with
// who holds each — a DE (digital employee: it runs the board, reads what happens on it and keeps
// its note), a person, or nobody yet. Shown on General and on "How it works"; a change is
// `PATCH boards/{id}` with the board's version.
import { NativeSelect, NativeSelectOption } from "@companyos/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import type { Api } from "../../../api/client";
import { orgChartQuery } from "../../chart/chart-queries";
import { problemText } from "../../settings/problem-text";
import type { Board } from "../view-engine";
import { invalidateWork } from "../work-queries";

type Holder = { membership_kind: string; display_name: string };

/** "Title · Name (DE)" — DE and person told apart, as every app labels them. */
export function seatLabel(title: string, holders: readonly Holder[]): string {
  const de = holders.find((h) => h.membership_kind === "digital_employee");
  const person = holders.find((h) => h.membership_kind === "person");
  const who = de ? `${de.display_name} (DE)` : person ? person.display_name : "vacant";
  return `${title} · ${who}`;
}

export function OwnerPicker({
  api,
  co,
  board,
  editable,
  label = "Owner",
}: {
  api: Api;
  co: string;
  board: Board;
  editable: boolean;
  label?: string;
}) {
  const id = useId();
  const qc = useQueryClient();
  const chart = useQuery(orgChartQuery(api, co));
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const seats = [...(chart.data?.seats ?? [])]
    .filter((s) => s.status !== "closed" || s.id === board.lead_seat_id)
    .sort((a, b) => a.title.localeCompare(b.title));
  const save = async (seatId: string | null) => {
    setPending(true);
    setError(null);
    try {
      await api.client.boards.update({
        params: { co, id: board.id },
        ifMatch: board.version,
        body: { lead_seat_id: seatId },
      });
      await invalidateWork(qc, co);
    } catch (e) {
      setError(problemText(e, "The owner could not be changed."));
    } finally {
      setPending(false);
    }
  };
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <NativeSelect
        id={id}
        aria-label={label}
        size="sm"
        disabled={!editable || pending}
        value={board.lead_seat_id ?? ""}
        onChange={(e) => void save(e.target.value || null)}
      >
        <NativeSelectOption value="">No owner</NativeSelectOption>
        {seats.map((s) => (
          <NativeSelectOption key={s.id} value={s.id}>
            {seatLabel(s.title, s.holders)}
          </NativeSelectOption>
        ))}
        {board.lead_seat_id && !seats.some((s) => s.id === board.lead_seat_id) ? (
          <NativeSelectOption value={board.lead_seat_id}>A seat you cannot see</NativeSelectOption>
        ) : null}
      </NativeSelect>
      {error ? (
        <p role="alert" className="text-xs text-danger-text">
          {error}
        </p>
      ) : null}
    </div>
  );
}
